import { useRef, type ReactNode } from "react";
import classNames from "classnames";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import { getNodeText } from "./boardCardText";
import styles from "./BoardCard.module.scss";

interface BoardCardSlotProps {
    children: ReactNode;
    className?: string;
}

/** First row: the title on the left, a status, badge or menu on the right. */
export const BoardCardHeader = ({
    children,
    className,
}: Readonly<BoardCardSlotProps>) => (
    <HStack
        gap={8}
        align="start"
        justify="between"
        className={classNames(styles.headerRow, className)}
    >
        {children}
    </HStack>
);

const isTitleTruncated = (title: HTMLSpanElement) =>
    isOverflowingX(
        title.querySelector<HTMLElement>(`.${styles.titleButtonText}`) ?? title,
    );

/**
 * Card title on one line, cut off with an ellipsis; hovering a cut-off title
 * shows all of it in a tooltip. A `BoardCardTitleButton` inside truncates the
 * same way; an inline rename field shows whole.
 */
export const BoardCardTitle = ({
    children,
    className,
}: Readonly<BoardCardSlotProps>) => {
    const ref = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref,
        isTruncated: isTitleTruncated,
    });

    return (
        <Tooltip content={getNodeText(children)} {...truncationTooltipProps}>
            <span ref={ref} className={classNames(styles.title, className)}>
                {children}
            </span>
        </Tooltip>
    );
};

/** Muted secondary row (client, entity, small chips). */
export const BoardCardMeta = ({
    children,
    className,
}: Readonly<BoardCardSlotProps>) => (
    <HStack
        gap={8}
        align="center"
        className={classNames(styles.meta, className)}
    >
        {children}
    </HStack>
);

/** Last row: chips on the left, people on the right. */
export const BoardCardFooter = ({
    children,
    className,
}: Readonly<BoardCardSlotProps>) => (
    <HStack
        gap={8}
        align="center"
        justify="between"
        className={classNames(styles.footer, className)}
    >
        {children}
    </HStack>
);
